/**
 * Copyright (c) 2026 Lauft App Inc. All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 */
import React, { useState, useEffect } from 'react';
import {
  X, Edit2, Key, MessageCircle, Trash2, RefreshCw, Video, Home, CreditCard, Mail, Phone
} from 'react-feather';
import AuthService from '../../services/authService';

const API_URL = 'https://back-end-x7tx.onrender.com/api';

/**
 * STUDIO AGENT DETAILS MODAL
 *
 * A Studio-only account ($29/mo AI listing videos) shares almost nothing with a
 * Pro agent. No territories, no lead pool, no claimed users, no invitations, no
 * priority tier, no points, no team, no referral links, no onboarding call.
 * Rendering the Pro modal for them means ten tabs of permanently empty state,
 * and — worse — a points field and a pool picker that would do real damage if
 * anyone used them.
 *
 * So this is a SEPARATE COMPONENT rather than conditionals inside
 * AgentDetailsModal. The Pro modal is ~3600 lines; threading `isStudio` through
 * it would leave both products sharing every future edit for no shared
 * behaviour. AgentDetailsModal delegates here and is otherwise untouched.
 *
 * WHO GETS THIS: entitlements includes 'studio' AND NOT 'pro'. A dual-
 * entitlement agent is a Pro agent who also bought Studio — they keep the full
 * Pro modal, which has its own Studio tab for credits.
 *
 * Everything here comes from GET /api/studio/admin/accounts/:id in one call.
 * The one exception is the MLS sync, which posts to the same
 * /api/mls/sync/agent/:id the Pro modal uses — the linking logic is identical
 * and there was no reason for a second one.
 */

const styles = {
  overlay: {
    position: 'fixed',
    top: 0, right: 0, bottom: 0,   // `left` applied at render — see sidebarOffset
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999999,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    width: '100%',
    height: '95vh',
    maxWidth: '1400px',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #e5e7eb',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.18)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #f0f0f0',
    flexShrink: 0,
  },
  content: {
    display: 'flex',
    gap: '24px',
    padding: '24px',
    flex: 1,
    minHeight: 0,
  },
  left: {
    width: '32%',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    overflowY: 'auto',
    paddingRight: '4px',
  },
  right: {
    width: '68%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f7f7f8',
    padding: '20px 24px',
    borderRadius: '12px',
    overflowY: 'auto',
  },
  card: {
    background: '#fafafa',
    borderRadius: '12px',
    padding: '16px',
    border: '1px solid #f0f0f0',
  },
  label: {
    fontSize: '10px', fontWeight: 600, letterSpacing: '0.08em',
    color: '#9ca3af', textTransform: 'uppercase',
  },
  closeButton: {
    background: 'none', border: 'none', color: '#6b7280',
    cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center',
  },
  actionBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
    width: '100%', padding: '10px 14px', fontSize: '13px', fontWeight: 600,
    borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff',
    color: '#374151', cursor: 'pointer',
  },
};

const STATUS_STYLE = {
  active:    { bg: '#f0fdf4', fg: '#15803d' },
  past_due:  { bg: '#fffbeb', fg: '#b45309' },
  cancelled: { bg: '#fef2f2', fg: '#b91c1c' },
};

const JOB_STYLE = {
  completed: { bg: '#f0fdf4', fg: '#15803d' },
  failed:    { bg: '#fef2f2', fg: '#b91c1c' },
  queued:    { bg: '#f3f4f6', fg: '#6b7280' },
  generating:{ bg: '#eff6ff', fg: '#1d4ed8' },
  rendering: { bg: '#eff6ff', fg: '#1d4ed8' },
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString() : '—');
const fmtPrice = (n) =>
  typeof n === 'number' ? `$${n.toLocaleString()}` : '—';

const StudioAgentDetailsModal = ({
  agent,
  onClose,
  onEdit,
  onAgentDeleted,
  onMessage,
  onResetPassword,
}) => {
  const [account, setAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [activeTab, setActiveTab] = useState('videos');

  // Same sidebar offset trick as the Pro modal: the overlay is position:fixed,
  // so it centres on the viewport and would slide under the floating sidebar.
  const [sidebarOffset, setSidebarOffset] = useState(0);
  useEffect(() => {
    const measure = () => {
      const el = document.querySelector('.dashboard-sidebar');
      const right = el ? el.getBoundingClientRect().right : 0;
      setSidebarOffset(right > 0 ? right : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Credits
  const [creditDelta, setCreditDelta] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [creditError, setCreditError] = useState('');
  const [creditSuccess, setCreditSuccess] = useState('');

  // Listings sync
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState('');

  // Free unwatermarked re-render
  const [isRerendering, setIsRerendering] = useState(false);
  const [rerenderError, setRerenderError] = useState('');
  const [rerenderSuccess, setRerenderSuccess] = useState('');

  const [isDeleting, setIsDeleting] = useState(false);

  const fetchAccount = async () => {
    if (!agent?._id) return;
    setIsLoading(true);
    setLoadError('');
    try {
      const response = await fetch(`${API_URL}/studio/admin/accounts/${agent._id}`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to load Studio account');
      }
      setAccount(data.data);
    } catch (err) {
      setLoadError(err.message || 'Failed to load Studio account');
      setAccount(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAccount();
    setCreditDelta('');
    setCreditReason('');
    setCreditError('');
    setCreditSuccess('');
    setSyncResult(null);
    setSyncError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?._id]);

  // Signed delta, never an absolute balance. The button disables while
  // in-flight because the endpoint has no idempotency key — a double click is
  // two grants, and this disable is the only thing preventing it.
  const submitAdjustment = async () => {
    const amount = parseInt(creditDelta, 10);
    const reason = creditReason.trim();

    if (!Number.isInteger(amount) || amount === 0) {
      setCreditError('Enter a non-zero whole number (negative to deduct)');
      return;
    }
    if (!reason) {
      setCreditError('A reason is required');
      return;
    }

    const confirmed = window.confirm(
      `${amount > 0 ? 'Grant' : 'Deduct'} ${Math.abs(amount)} credit` +
      `${Math.abs(amount) === 1 ? '' : 's'} ` +
      `${amount > 0 ? 'to' : 'from'} ${account?.name || 'this account'}?\n\n` +
      `Reason: ${reason}\n\n` +
      `Each credit is one video render (~$4-5 of compute). Logged against ` +
      `your admin account.`
    );
    if (!confirmed) return;

    setIsAdjusting(true);
    setCreditError('');
    setCreditSuccess('');

    try {
      const response = await fetch(`${API_URL}/studio/admin/accounts/${agent._id}/credits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ delta: amount, reason })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Adjustment failed');
      }

      setCreditSuccess(
        `${amount > 0 ? 'Granted' : 'Deducted'} ${Math.abs(amount)} credit` +
        `${Math.abs(amount) === 1 ? '' : 's'} — balance is now ${data.data.credits}`
      );
      setCreditDelta('');
      setCreditReason('');
      // Refetch for the stored balanceAfter rather than patching local state.
      fetchAccount();
    } catch (err) {
      setCreditError(err.message || 'Adjustment failed');
    } finally {
      setIsAdjusting(false);
    }
  };

  // Same endpoint the Pro modal uses. Matches MLS listings to this agent by
  // email / phone / mlsCode and links them via listedBy.agentId.
  const handleSyncListings = async () => {
    const confirmed = window.confirm(
      `Sync MLS listings for ${account?.name || agent.name}?\n\n` +
      `Searches the MLS for their active listings, imports them, and links ` +
      `them to this account.\n\nThis may take 30-90 seconds.`
    );
    if (!confirmed) return;

    setIsSyncing(true);
    setSyncResult(null);
    setSyncError('');

    try {
      const response = await fetch(`${API_URL}/mls/sync/agent/${agent._id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to sync listings');
      }
      setSyncResult(data);
      fetchAccount();     // listing count just changed
    } catch (err) {
      setSyncError(err.message || 'Failed to sync listings');
    } finally {
      setIsSyncing(false);
    }
  };

  // Triggers the account's one free unwatermarked copy of their teaser. Costs
  // them nothing and lands in their own library automatically — it's a normal
  // VideoJob with their agentId and source 'studio'.
  //
  // One per account. A second press returns the existing job instead of
  // rendering again, so the button disables itself once a clean copy exists.
  const handleRerender = async (job) => {
    const confirmed = window.confirm(
      `Generate the unwatermarked version of "${job.listingId?.address || 'this listing'}" ` +
      `for ${account?.name || agent.name}?\n\n` +
      `Listing details included, no agent branding.\n\n` +
      `Free to them — does not touch their credits. Costs us one render ` +
      `(~$4-5). It appears in their library in about two minutes.\n\n` +
      `One per account.`
    );
    if (!confirmed) return;

    setIsRerendering(true);
    setRerenderError('');
    setRerenderSuccess('');

    try {
      const response = await fetch(`${API_URL}/studio/admin/accounts/${agent._id}/teaser-rerender`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Re-render failed');
      }

      setRerenderSuccess(
        data.data.alreadyClaimed
          ? 'This account already has its free unwatermarked copy — no new render started.'
          : 'Clean version queued. It will appear in their library in about two minutes.'
      );
      fetchAccount();
    } catch (err) {
      setRerenderError(err.message || 'Re-render failed');
    } finally {
      setIsRerendering(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${agent.name}? This cannot be undone.`)) return;
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_URL}/admin/agents/${agent._id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        onAgentDeleted?.();
        onClose();
      } else {
        setLoadError(data.message || 'Failed to delete account');
      }
    } catch (err) {
      setLoadError('Error deleting account');
    } finally {
      setIsDeleting(false);
    }
  };

  if (!agent) return null;

  const statusStyle = STATUS_STYLE[account?.studioStatus] || STATUS_STYLE.active;

  // A free clean copy is source 'studio' at zero credits with a parent job —
  // the same shape performTeaserRerender creates. Used to hide the button once
  // it's been done, so the admin isn't offered an action that returns "already
  // claimed".
  const hasCleanCopy = (account?.videoJobs || []).some(
    (j) => j.source === 'studio' && j.creditsCost === 0 && j.parentJobId
  );

  return (
    <div style={{ ...styles.overlay, left: sidebarOffset }} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        {/* ---- Header ---- */}
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#111', letterSpacing: '-0.01em' }}>
              Studio Account
            </h3>
            <span style={{
              padding: '4px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 700,
              background: 'rgba(17,17,17,0.06)', color: '#374151', letterSpacing: '0.04em'
            }}>
              STUDIO
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => onEdit?.(agent)} style={{ padding: '8px 14px' }}>
              <Edit2 size={16} /> Edit
            </button>
            <button onClick={onClose} style={styles.closeButton}>
              <X size={22} />
            </button>
          </div>
        </div>

        <div style={styles.content}>

          {/* ---- LEFT: identity, credits, actions ---- */}
          <div style={styles.left}>

            <div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#111' }}>
                {account?.name || agent.name}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280', fontSize: '12px', marginTop: '6px' }}>
                <Mail size={13} /> {account?.email || agent.email}
              </div>
              {(account?.phone || agent.phone) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
                  <Phone size={13} /> {account?.phone || agent.phone}
                </div>
              )}
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
                Joined {fmtDate(account?.createdAt)} · last login {fmtDate(account?.lastLoginAt)}
              </div>
            </div>

            {/* Credits */}
            <div style={styles.card}>
              <div style={styles.label}>Credits</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginTop: '4px' }}>
                <div style={{ fontSize: '32px', fontWeight: 700, color: '#111', lineHeight: 1.1 }}>
                  {account?.credits ?? '—'}
                </div>
                <div style={{
                  padding: '3px 9px', borderRadius: '999px', fontSize: '11px', fontWeight: 600,
                  background: statusStyle.bg, color: statusStyle.fg,
                }}>
                  {account?.studioStatus || '—'}
                </div>
              </div>
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
                Granted {fmtDate(account?.creditsGrantedAt)}
                {account && ` · ${account.netManual > 0 ? '+' : ''}${account.netManual} comped`}
              </div>
            </div>

            {/* Listings */}
            <div style={styles.card}>
              <div style={styles.label}>Listings</div>
              <div style={{ fontSize: '32px', fontWeight: 700, color: '#111', lineHeight: 1.1, marginTop: '4px' }}>
                {account?.listingCount ?? '—'}
              </div>
              <button
                onClick={handleSyncListings}
                disabled={isSyncing}
                style={{
                  ...styles.actionBtn,
                  marginTop: '12px',
                  cursor: isSyncing ? 'not-allowed' : 'pointer',
                  opacity: isSyncing ? 0.6 : 1,
                }}
              >
                <RefreshCw size={14} style={{ animation: isSyncing ? 'spin 1s linear infinite' : 'none' }} />
                {isSyncing ? 'Syncing MLS…' : 'Sync MLS Listings'}
              </button>

              {syncError && (
                <div style={{ fontSize: '11px', color: '#b91c1c', marginTop: '10px' }}>{syncError}</div>
              )}
              {syncResult && !isSyncing && (
                <div style={{ fontSize: '11px', color: '#374151', marginTop: '10px', lineHeight: 1.6 }}>
                  Synced {syncResult.found} active listing{syncResult.found === 1 ? '' : 's'} —{' '}
                  <strong>{syncResult.created}</strong> new,{' '}
                  <strong>{syncResult.updated}</strong> updated,{' '}
                  <strong>{syncResult.linkedExisting}</strong> newly linked
                  {syncResult.errors > 0 && (
                    <span style={{ color: '#b91c1c' }}>, <strong>{syncResult.errors}</strong> errors</span>
                  )}
                </div>
              )}
            </div>

            {/* Brand kit — read-only. It's the customer's own branding; an admin
                editing it silently changes what gets burned into their videos. */}
            {account?.brandKit && (
              <div style={styles.card}>
                <div style={styles.label}>Brand kit</div>
                {account.brandKit.complete ? (
                  <div style={{ fontSize: '12px', color: '#374151', marginTop: '8px', lineHeight: 1.7 }}>
                    <div>{account.brandKit.displayName}</div>
                    <div style={{ color: '#6b7280' }}>{account.brandKit.brokerage}</div>
                    {account.brandKit.licenseNumber && (
                      <div style={{ color: '#9ca3af', fontSize: '11px' }}>Lic. {account.brandKit.licenseNumber}</div>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
                    Not set up — their videos render unbranded.
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button style={styles.actionBtn} onClick={() => onMessage?.(agent)}>
                <MessageCircle size={14} /> Message
              </button>
              <button style={styles.actionBtn} onClick={() => onResetPassword?.(agent)}>
                <Key size={14} /> Reset password
              </button>
              {account?.stripeCustomerId && (
                <a
                  href={`https://dashboard.stripe.com/customers/${account.stripeCustomerId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ ...styles.actionBtn, textDecoration: 'none' }}
                >
                  <CreditCard size={14} /> Billing in Stripe
                </a>
              )}
              <button
                style={{ ...styles.actionBtn, color: '#b91c1c', borderColor: '#fecaca' }}
                onClick={handleDelete}
                disabled={isDeleting}
              >
                <Trash2 size={14} /> {isDeleting ? 'Deleting…' : 'Delete account'}
              </button>
            </div>
          </div>

          {/* ---- RIGHT: videos, listings, credit history ---- */}
          <div style={styles.right}>

            {loadError && (
              <div style={{
                background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
                padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px'
              }}>{loadError}</div>
            )}

            {isLoading && !account && (
              <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                Loading Studio account…
              </div>
            )}

            {account && (
              <>
                <div style={{
                  display: 'flex', gap: '4px', borderBottom: '1px solid #e5e7eb',
                  marginBottom: '20px', overflowX: 'auto', flexShrink: 0,
                }}>
                  {[
                    { key: 'videos',   label: `Videos (${account.videoJobCount})` },
                    { key: 'listings', label: `Listings (${account.listingCount})` },
                    { key: 'credits',  label: `Credits (${account.adjustments.length})` },
                  ].map(tab => (
                    <div
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      style={{
                        padding: '10px 18px', cursor: 'pointer', fontSize: '0.9rem',
                        fontWeight: 600, whiteSpace: 'nowrap', userSelect: 'none',
                        color: activeTab === tab.key ? '#111' : '#9ca3af',
                        borderBottom: activeTab === tab.key ? '2px solid #111' : '2px solid transparent',
                        marginBottom: '-1px', transition: 'color 0.2s',
                      }}
                    >
                      {tab.label}
                    </div>
                  ))}
                </div>

                {/* ---- VIDEOS ---- */}
                {activeTab === 'videos' && (
                  <>
                  {rerenderError && (
                    <div style={{
                      background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
                      padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px'
                    }}>{rerenderError}</div>
                  )}
                  {rerenderSuccess && (
                    <div style={{
                      background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d',
                      padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px'
                    }}>{rerenderSuccess}</div>
                  )}
                  {account.videoJobs.length === 0 ? (
                    <div style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                      No video jobs yet.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {account.videoJobs.map((job) => {
                        const js = JOB_STYLE[job.status] || JOB_STYLE.queued;
                        return (
                          <div key={job._id} style={{
                            background: '#fff', border: '1px solid #f0f0f0', borderRadius: '10px',
                            padding: '12px 14px', display: 'flex', gap: '12px', alignItems: 'flex-start'
                          }}>
                            <Video size={15} style={{ color: '#9ca3af', marginTop: '2px', flexShrink: 0 }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '13px', fontWeight: 600, color: '#111' }}>
                                  {job.listingId?.address || 'Listing removed'}
                                </span>
                                <span style={{
                                  padding: '2px 8px', borderRadius: '999px', fontSize: '10px',
                                  fontWeight: 700, background: js.bg, color: js.fg,
                                }}>
                                  {job.status}
                                </span>
                                {/* WATERMARKED. source is the only input to the
                                    watermark decision — videoWorker.buildRailwayPayload
                                    sets watermark.enabled off `source === 'teaser'`,
                                    and it's burned into the frames, so this badge is
                                    accurate for every job without checking the file. */}
                                {job.source === 'teaser' && (
                                  <span style={{
                                    padding: '2px 8px', borderRadius: '999px', fontSize: '10px',
                                    fontWeight: 700, background: '#fffbeb', color: '#b45309',
                                  }}>
                                    watermarked
                                  </span>
                                )}
                                {/* The free clean copy: source 'studio' at zero cost,
                                    linked back to the teaser it replaced. */}
                                {job.source === 'studio' && job.creditsCost === 0 && job.parentJobId && (
                                  <span style={{
                                    padding: '2px 8px', borderRadius: '999px', fontSize: '10px',
                                    fontWeight: 700, background: '#f0fdf4', color: '#15803d',
                                  }}>
                                    free clean copy
                                  </span>
                                )}
                                {job.creditsRefunded && (
                                  <span style={{
                                    padding: '2px 8px', borderRadius: '999px', fontSize: '10px',
                                    fontWeight: 700, background: '#fffbeb', color: '#b45309',
                                  }}>
                                    refunded
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                                {job.listingId?.csz ? `${job.listingId.csz} · ` : ''}
                                {fmtDateTime(job.createdAt)}
                                {job.creditsCost ? ` · ${job.creditsCost} credit${job.creditsCost === 1 ? '' : 's'}` : ' · free'}
                                {job.options?.formatPresets?.length ? ` · ${job.options.formatPresets.join(', ')}` : ''}
                              </div>
                              {job.status === 'failed' && job.failureReason && (
                                <div style={{ fontSize: '11px', color: '#b91c1c', marginTop: '4px' }}>
                                  {job.failureReason}
                                </div>
                              )}
                            </div>
                            {/* Only on a COMPLETED teaser, and only while no clean
                                copy exists. A failed teaser already handed the free
                                generation back, so the fix there is regenerating the
                                teaser, not burning this. */}
                            {job.source === 'teaser' && job.status === 'complete' && !hasCleanCopy && (
                              <button
                                onClick={() => handleRerender(job)}
                                disabled={isRerendering}
                                style={{
                                  fontSize: '11px', fontWeight: 600, color: '#fff',
                                  padding: '6px 11px', border: 'none', borderRadius: '6px',
                                  background: isRerendering ? '#d1d5db' : '#111',
                                  cursor: isRerendering ? 'default' : 'pointer',
                                  whiteSpace: 'nowrap', flexShrink: 0, alignSelf: 'center'
                                }}
                              >
                                {isRerendering ? 'Queueing…' : 'Generate clean version'}
                              </button>
                            )}
                            {job.outputs?.length > 0 && (
                              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                {job.outputs.map((out, i) => (
                                  <a
                                    key={i}
                                    href={out.cloudinaryUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      fontSize: '11px', fontWeight: 600, color: '#111',
                                      padding: '5px 10px', border: '1px solid #e5e7eb',
                                      borderRadius: '6px', textDecoration: 'none', whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {out.format || 'view'}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  </>
                )}

                {/* ---- LISTINGS ---- */}
                {activeTab === 'listings' && (
                  account.listings.length === 0 ? (
                    <div style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                      No listings linked. Run an MLS sync from the left panel.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {account.listingsTruncated && (
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>
                          Showing the 100 most recent of {account.listingCount}.
                        </div>
                      )}
                      {account.listings.map((p) => (
                        <div key={p._id} style={{
                          background: '#fff', border: '1px solid #f0f0f0', borderRadius: '10px',
                          padding: '12px 14px', display: 'flex', gap: '12px', alignItems: 'center'
                        }}>
                          {p.mainImage ? (
                            <img
                              src={p.mainImage}
                              alt=""
                              style={{ width: '52px', height: '40px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }}
                            />
                          ) : (
                            <div style={{
                              width: '52px', height: '40px', borderRadius: '6px', background: '#f3f4f6',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                            }}>
                              <Home size={14} style={{ color: '#d1d5db' }} />
                            </div>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: '#111' }}>{p.address}</div>
                            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '3px' }}>
                              {p.csz}
                              {p.bedrooms ? ` · ${p.bedrooms}bd` : ''}
                              {p.bathrooms ? ` · ${p.bathrooms}ba` : ''}
                              {p.sqft ? ` · ${p.sqft.toLocaleString()} sqft` : ''}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: '#111' }}>{fmtPrice(p.price)}</div>
                            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '3px' }}>{p.status}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}

                {/* ---- CREDITS ---- */}
                {activeTab === 'credits' && (
                  <div>
                    {creditError && (
                      <div style={{
                        background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
                        padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px'
                      }}>{creditError}</div>
                    )}
                    {creditSuccess && (
                      <div style={{
                        background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d',
                        padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px'
                      }}>{creditSuccess}</div>
                    )}

                    <div style={{ ...styles.card, background: '#fff', marginBottom: '20px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: '#111', marginBottom: '4px' }}>
                        Adjust credits
                      </div>
                      <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '14px' }}>
                        Negative deducts. Capped at 20 either way. One credit is one video render.
                      </div>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '11px', color: '#6b7280', marginBottom: '5px' }}>
                            Amount
                          </label>
                          <input
                            type="number"
                            value={creditDelta}
                            onChange={(e) => { setCreditDelta(e.target.value); setCreditError(''); }}
                            placeholder="+2"
                            style={{ width: '90px', padding: '9px 10px', fontSize: '13px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: '240px' }}>
                          <label style={{ display: 'block', fontSize: '11px', color: '#6b7280', marginBottom: '5px' }}>
                            Reason (required, logged)
                          </label>
                          <input
                            type="text"
                            value={creditReason}
                            onChange={(e) => { setCreditReason(e.target.value); setCreditError(''); }}
                            placeholder="comp — failed render, refund didn't fire"
                            style={{ width: '100%', padding: '9px 10px', fontSize: '13px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                          />
                        </div>
                        <button
                          onClick={submitAdjustment}
                          disabled={isAdjusting}
                          style={{
                            padding: '10px 18px', fontSize: '12px', fontWeight: 600,
                            borderRadius: '8px', border: 'none', color: '#fff',
                            background: isAdjusting ? '#d1d5db' : '#111',
                            cursor: isAdjusting ? 'default' : 'pointer', whiteSpace: 'nowrap'
                          }}
                        >
                          {isAdjusting ? 'Applying…' : 'Apply'}
                        </button>
                      </div>
                    </div>

                    <div style={{ ...styles.card, background: '#fff' }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: '#111', marginBottom: '12px' }}>
                        Manual adjustments
                      </div>
                      {account.adjustments.length === 0 ? (
                        <div style={{ fontSize: '12px', color: '#9ca3af', padding: '8px 0' }}>
                          None. Subscription grants and video charges aren't shown here.
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          {account.adjustments.map((adj, i) => (
                            <div key={i} style={{
                              display: 'flex', gap: '12px', alignItems: 'baseline', padding: '9px 0',
                              borderBottom: i < account.adjustments.length - 1 ? '1px solid #f0f0f0' : 'none'
                            }}>
                              <div style={{
                                fontSize: '13px', fontWeight: 700, minWidth: '34px',
                                color: adj.delta > 0 ? '#15803d' : '#b91c1c'
                              }}>
                                {adj.delta > 0 ? '+' : ''}{adj.delta}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '12px', color: '#374151' }}>{adj.reason}</div>
                                <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                                  {adj.adminEmail || 'unknown admin'} · {fmtDateTime(adj.at)}
                                  {adj.balanceAfter != null && ` · balance ${adj.balanceAfter}`}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudioAgentDetailsModal;